import { create } from 'zustand';
import { applyLivePlayStateWsPayloadToEntry } from '@/helpers/merge-live-play-state.helper';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum.ts';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';

type EntityRoomKey = `${MediaParentEntity}::${string}`;

type PlayStateByPlayer = NonNullable<LiveContextStateDto['playState']>['byPlayer'];

/** Mirror of the service `LiveContextPlayStateWsPayloadV1`. */
export interface LiveContextPlayStateWsPayloadV1 {
  version: 1;
  entityRef: string;
  seq: number;
  emittedAt: number;
  byPlayer: NonNullable<PlayStateByPlayer>;
  showMode?: string;
  activeAgentDeckMediaId?: string;
  /** Empty string clears Control Next look-ahead; omitted leaves prior meta alone. */
  lookAheadItemId?: string;
  lookAheadShowMode?: string;
  lookAheadScope?: string;
  lookAheadSlotId?: string;
  lookAhead2ItemId?: string;
  lookAhead2ShowMode?: string;
  lookAhead2Scope?: string;
  lookAhead2SlotId?: string;
  lookAheadInsertResumeItemId?: string;
  lookAheadInsertResumeShowMode?: string;
  lookAheadInsertResumeScope?: string;
  lookAheadInsertResumeSlotId?: string;
  lookAheadPrevItemId?: string;
  lookAheadPrevShowMode?: string;
  lookAheadPrevScope?: string;
  lookAheadPrevSlotId?: string;
  presentationTransitionInFlight?: boolean;
  backupSlides?: boolean;
  /** On Air camera focus. Omitted when the emit does not know it. */
  focusMode?: string;
  /** `false` clears the on-stage toggle. Omitted leaves the previous value. */
  remoteSpeakersOnStage?: boolean;
}

export type LivePlayStateEntry = {
  /** Client clock when the payload was applied (debug / telemetry; merge uses content, not this). */
  receivedAt: number;
  byPlayer: NonNullable<PlayStateByPlayer>;
  showMode?: string;
  /** Undefined means no deck is on air — the slide controls key off this. */
  activeAgentDeckMediaId?: string;
  lookAheadItemId?: string;
  lookAheadShowMode?: string;
  lookAheadScope?: string;
  lookAheadSlotId?: string;
  lookAhead2ItemId?: string;
  lookAhead2ShowMode?: string;
  lookAhead2Scope?: string;
  lookAhead2SlotId?: string;
  lookAheadInsertResumeItemId?: string;
  lookAheadInsertResumeShowMode?: string;
  lookAheadInsertResumeScope?: string;
  lookAheadInsertResumeSlotId?: string;
  lookAheadPrevItemId?: string;
  lookAheadPrevShowMode?: string;
  lookAheadPrevScope?: string;
  lookAheadPrevSlotId?: string;
  presentationTransitionInFlight?: boolean;
  backupSlides?: boolean;
  focusMode?: string;
  remoteSpeakersOnStage?: boolean;
  /** Last applied `live-context-play-state` seq. */
  seq?: number;
};

type LivePlayStateWsSlice = {
  lastSeqByRoom: Record<string, number>;
  playStateByRoom: Record<string, LivePlayStateEntry>;
  /** @returns true when the payload was applied (seq advanced). */
  applyPayload: (roomKey: EntityRoomKey, payload: LiveContextPlayStateWsPayloadV1) => boolean;
  /**
   * Drop the transitional preview overlay but keep the seq watermark so a delayed older
   * `live-context-play-state` cannot re-apply after the post-command GET.
   */
  clearPlayState: (roomKey: EntityRoomKey) => void;
  resetRoom: (roomKey: EntityRoomKey) => void;
};

export const useLivePlayStateWsStore = create<LivePlayStateWsSlice>((set, get) => ({
  lastSeqByRoom: {},
  playStateByRoom: {},
  applyPayload: (roomKey, payload) => {
    if (payload.version !== 1 || typeof payload.seq !== 'number') {
      return false;
    }
    const last = get().lastSeqByRoom[roomKey] ?? 0;
    if (payload.seq <= last) {
      return false;
    }
    set({
      lastSeqByRoom: { ...get().lastSeqByRoom, [roomKey]: payload.seq },
      playStateByRoom: {
        ...get().playStateByRoom,
        [roomKey]: applyLivePlayStateWsPayloadToEntry(get().playStateByRoom[roomKey], payload),
      },
    });
    return true;
  },
  clearPlayState: (roomKey) => {
    set((s) => {
      if (!(roomKey in s.playStateByRoom)) {
        return s;
      }
      const playStateByRoom = { ...s.playStateByRoom };
      delete playStateByRoom[roomKey];
      return { playStateByRoom };
    });
  },
  resetRoom: (roomKey) => {
    set((s) => {
      const lastSeqByRoom = { ...s.lastSeqByRoom };
      delete lastSeqByRoom[roomKey];
      const playStateByRoom = { ...s.playStateByRoom };
      delete playStateByRoom[roomKey];
      return { lastSeqByRoom, playStateByRoom };
    });
  },
}));

/** Non-React entry for the socket handler. True when this seq was applied. */
export function applyLivePlayStateWsPayload(roomKey: EntityRoomKey, payload: LiveContextPlayStateWsPayloadV1): boolean {
  return useLivePlayStateWsStore.getState().applyPayload(roomKey, payload);
}

/** After a post-commit live-context GET — drop the overlay, keep seq. */
export function clearLivePlayStateOverlay(roomKey: EntityRoomKey) {
  useLivePlayStateWsStore.getState().clearPlayState(roomKey);
}

export function resetLivePlayStateRoom(roomKey: EntityRoomKey) {
  useLivePlayStateWsStore.getState().resetRoom(roomKey);
}
